import React, { useState } from "react";
import {
  StyledOuterPopup,
  StyledInnerPopup_Small,
  StyledPopupForm,
  StyledPopupErrorMessage,
} from "../../styles/components/Popup.styled";
import { Button } from "../../styles/components/Buttons.styled";
import { createApiClient } from "../../utils";
import { useAccountContext } from "../../contexts/Account";
import { useScrollLock } from "../../hooks";

interface UpdateAdminPopupProps {
  currentAdmin: string;
  onClose: () => void;
}

export function UpdateAdminPopup({
  currentAdmin,
  onClose,
}: UpdateAdminPopupProps) {
  const [email, setEmail] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { getRefreshedToken } = useAccountContext();

  // Lock scrolling when modal is open
  useScrollLock(true);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSuccessMessage("");
    setErrorMessage("");

    if (!email.trim()) {
      setErrorMessage("Please enter an email address");
      return;
    }

    setIsSubmitting(true);

    try {
      const token = await getRefreshedToken();
      const api = createApiClient(token);

      // TODO: Update this URL to match your actual API endpoint
      const response = await api.post(
        `${process.env.REACT_APP_AWS_ITEM_API_URL}admin/search-and-notify`,
        { adminEmail: email.trim() }
      );

      setSuccessMessage(
        `An invitation has been sent to ${email}. Once they accept, they will become your administrator.`
      );
      setEmail("");

      // Auto-close after 3 seconds on success
      setTimeout(() => {
        onClose();
      }, 3000);
    } catch (err: any) {
      console.error("Error updating admin:", err);

      if (err.response?.status === 404) {
        setErrorMessage("No administrator account found with that email address");
      } else if (err.response?.data?.message) {
        setErrorMessage(err.response.data.message);
      } else {
        setErrorMessage("Failed to send admin update request. Please try again.");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <StyledOuterPopup>
      <StyledInnerPopup_Small>
        <h2>Update Administrator</h2>
        <p>Current Administrator: <strong>{currentAdmin}</strong></p>
        <p>
          Enter the email address of the administrator you would like to
          transfer to. An invitation will be sent to them for approval.
        </p>

        <StyledPopupForm onSubmit={handleSubmit}>
          <label htmlFor="admin-email">New Administrator Email:</label>
          <input
            id="admin-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="admin@example.com"
            disabled={isSubmitting}
            required
          />

          {successMessage && (
            <div style={{ color: "#00ff00", marginBottom: "15px" }}>
              {successMessage}
            </div>
          )}

          {errorMessage && (
            <StyledPopupErrorMessage style={{ marginBottom: "15px" }}>
              {errorMessage}
            </StyledPopupErrorMessage>
          )}

          <div>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Sending..." : "Send Invitation"}
            </Button>
            <Button type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
          </div>
        </StyledPopupForm>
      </StyledInnerPopup_Small>
    </StyledOuterPopup>
  );
}
